'use client';

import { HandsFreePopupMessageRenderer } from '@/shared-project-response-message-renderers';
import { ReportProblem, type Messages } from '@/shared-ui';
import { OptionRenderType } from '@/survey-graphql';

import { getTypeOfRenderer } from './project-response-chat.helpers';
import { enumStringEquals } from './project-response.helpers';
import { useHandsFreePopupSelection } from './project-usability-handsfree-popup-context';
import { useHandsFreeQuestionPopupContext } from './project-usability-handsfree-question-popup-context';

type ChatMessage = Messages[number];

interface Props {
  replyFeedbackMessage?: ChatMessage;
  isConversationOver?: boolean;
  isSubmitting?: boolean;
  awaitingTaskCompletion?: boolean;
  onSubmitContent?: (content: string) => void;
  onReportProblemSubmit?: (
    description: string,
    context: { messageId?: string; questionId?: string }
  ) => void;
}

export default function ProjectUsabilityHandsFreeQuestionPopupBody({
  replyFeedbackMessage,
  isConversationOver = false,
  isSubmitting = false,
  awaitingTaskCompletion = false,
  onSubmitContent,
  onReportProblemSubmit,
}: Props) {
  const selection = useHandsFreePopupSelection();

  const { message, messageType, projectType } =
    useHandsFreeQuestionPopupContext();

  return (
    <>
      {replyFeedbackMessage && (
        <>
          <HandsFreePopupMessageRenderer
            type={getTypeOfRenderer(
              {
                ...replyFeedbackMessage,
                createdAt: new Date(replyFeedbackMessage.createdAt),
                optionRenderType:
                  replyFeedbackMessage.optionRenderType ??
                  OptionRenderType.Text,
              },
              projectType
            )}
            text={replyFeedbackMessage.text}
            options={replyFeedbackMessage.options}
            optionRenderType={replyFeedbackMessage.optionRenderType}
            contentUrl={replyFeedbackMessage.contentUrl}
          />
          <hr className='border-gray-100' />
        </>
      )}

      <HandsFreePopupMessageRenderer
        key={message.id}
        type={messageType}
        text={message.text}
        options={message.options}
        optionRenderType={
          enumStringEquals(message.optionRenderType, OptionRenderType.Media)
            ? OptionRenderType.Text
            : message.optionRenderType
        }
        assets={message.assets}
        contentUrl={message.contentUrl}
        onSubmitContent={onSubmitContent}
        isSubmitting={isSubmitting}
        awaitingTaskCompletion={awaitingTaskCompletion}
        maxSelections={message.maxSelections ?? undefined}
        isConversationOver={isConversationOver}
        value={selection?.value}
        onChange={selection?.onChange}
        subQuestions={message.subQuestions}
        notApplicableLabel={message.notApplicableLabel}
      />

      {onReportProblemSubmit && (
        <ReportProblem
          context={{
            messageId: message.id,
            questionId: message.questionId ?? undefined,
          }}
          onSubmit={onReportProblemSubmit}
          variant='main'
          className='mt-2 justify-center text-gray-700'
        />
      )}
    </>
  );
}
